import { hash } from '@ember/helper';
import { action, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDatepickersDatepickerSharedTemplate from 'lumen/components/general/datepickers/datepicker-shared-template';
import GeneralDropdownsSidebar from 'lumen/components/general/dropdowns/sidebar';

type SidebarList = {
  options: {
    /** Fills `{count}` in labels like "Next {count} days", so each range needs no translation of its own. */
    count?: number;
    id: string;
    range: {
      end: Dayjs;
      start: Dayjs;
    };
    text: string;
  }[];
  title: string;
};

export interface GeneralDatepickersDatepickerWithSidebarSignature {
  Args: {
    calendarFocusOnEnd?: boolean;
    disabledTooltipMessage?: string;
    footerMessage?: string;
    isApplyDisabled?: boolean;
    isDisabled?: boolean;
    maxDate?: Dayjs;
    maxRange?: number | string;
    minDate?: Dayjs;
    onCancel?: () => void;
    onUpdateTempRange?: (range: { end: Dayjs | null; start: Dayjs }) => void;
    range: { end: Dayjs | null; start: Dayjs };
    rangeChanged: (
      range: { end: Dayjs | null; start: Dayjs },
      optionId: string,
    ) => void;
    sidebarLists: SidebarList[];
    triggerClass?: string;
    triggerLabel?: string;
    triggerLabelFormatter?: (start: Dayjs, end: Dayjs) => string;
  };
  Blocks: {
    default: [
      {
        refocusCalendar: () => void;
      },
    ];
  };
}

// @ts-expect-error: Incorrect type
export default class GeneralDatepickersDatepickerWithSidebar extends GeneralDatepickersDatepickerRange<GeneralDatepickersDatepickerWithSidebarSignature> {
  @service declare usageAnalytics: Services['usage-analytics'];

  tempOption = null;

  @action
  // @ts-expect-error: Incorrect type
  applyChanges(dropdown): void {
    this.trackApplyChanges();

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.changeRangeTask.perform(this.tempRange, this.tempOption.id);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    dropdown.toggleDropdown();
  }

  @action
  // @ts-expect-error: Incorrect type
  selectOption(option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.updateTempRange(option.convertedRange, option.id);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.onUpdateTempRange?.(option.convertedRange);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.refocusCalendarTask.perform();
  }

  @action
  trackApplyChanges(): void {
    // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
    // @ts-expect-error: Incorrect type
    const isCustomOption = this.tempOption.id === 'custom';
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const range = isCustomOption ? this.tempRange : this.tempOption.range;

    this.usageAnalytics.track('Stay Date Range Changed', {
      eventTrigger: isCustomOption
        ? 'date-picker-custom'
        : 'date-picker-predefined',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      amountOfDays: (range.end?.diff(range.start, 'days') ?? 0) + 1,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      startDaysOffsetFromToday: range.start.diff(this.today, 'days'),
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  updateTempRange(range, optionId): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (range.start) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.tempRange = range;
    }

    let customOption = null;
    let hasOptionSelected = false;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.convertedSidebarLists.forEach((list) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      list.options.forEach((listOption) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        set(listOption, 'isSelected', false);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (listOption.id === 'custom') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          customOption = listOption;
        }
        if (!hasOptionSelected) {
          if (optionId) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (optionId === listOption.id) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              set(listOption, 'isSelected', true);
              set(this, 'tempOption', listOption);
              hasOptionSelected = true;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          } else if (listOption.range.start) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const isSameStartOfRange = listOption.convertedRange.start.isSame(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.tempRange.start,
              'day',
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const isSameEndOfRange = listOption.convertedRange.end.isSame(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.tempRange.end,
              'day',
            );

            if (isSameStartOfRange && isSameEndOfRange) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              set(listOption, 'isSelected', true);
              set(this, 'tempOption', listOption);
              hasOptionSelected = true;
            }
          }
        }
      });
    });

    if (!hasOptionSelected && customOption) {
      set(customOption, 'isSelected', true);
      set(this, 'tempOption', customOption);
    }
  }

  <template>
    <GeneralDatepickersDatepickerSharedTemplate
      @applyChanges={{this.applyChanges}}
      @beginReached={{this.beginReached}}
      @calendarFocusOnEnd={{@calendarFocusOnEnd}}
      @canSelectMonth={{this.canSelectMonth}}
      @cancelChanges={{this.cancelChanges}}
      @dateTooltips={{@dateTooltips}}
      @disabledTooltipMessage={{@disabledTooltipMessage}}
      @displayEvents={{@displayEvents}}
      @endReached={{this.endReached}}
      @footerMessage={{@footerMessage}}
      {{! @glint-expect-error: Incorrect type }}
      @hasEventAccess={{this.access.hasEvents}}
      @isApplyDisabled={{this.isApplyDisabled}}
      @isDisabled={{@isDisabled}}
      @maxDate={{this.convertedMaxDate}}
      @maxRange={{@maxRange}}
      @minDate={{this.convertedMinDate}}
      @nextClicked={{this.nextClicked}}
      @onCancel={{this.onCancel}}
      @previousClicked={{this.previousClicked}}
      @range={{this.convertedRange}}
      @refocusMonths={{this.refocusMonths}}
      @selectRange={{this.selectRange}}
      @showMonthDropdowns={{@showMonthDropdowns}}
      @standardRange={{@standardRange}}
      @tempRange={{this.tempRange}}
      @triggerClass={{@triggerClass}}
      @triggerLabel={{@triggerLabel}}
      @triggerLabelFormatter={{@triggerLabelFormatter}}
      @updateTempRange={{this.updateTempRange}}
    >
      <:beforeContent>
        {{yield (hash refocusCalendar=this.refocusCalendarTask.perform)}}
      </:beforeContent>
      <:sidebar>
        <GeneralDropdownsSidebar
          @selectOption={{this.selectOption}}
          @sidebarLists={{this.convertedSidebarLists}}
        />
      </:sidebar>
    </GeneralDatepickersDatepickerSharedTemplate>
  </template>
}
